import { motion, useTransform } from 'motion/react';
import type { MotionValue } from 'motion/react';
import { useCallback, useRef } from 'react';
import { ScrollBarTrack } from './ScrollBar.styles';
import useElementSize from './useElementSize';

type ScrollBarProps = {
  scrollPercent: MotionValue<number>;
  onScrollPercentChange: (percent: number, smooth?: boolean) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  thumbWidth: number;
};

export default function ScrollBar({
  scrollPercent,
  onScrollPercentChange,
  onDragStart,
  onDragEnd,
  thumbWidth
}: ScrollBarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const { width: trackWidth } = useElementSize(trackRef);

  const thumbPosition = useTransform(scrollPercent, percentValue => {
    const maxThumbPosition = Math.max(trackWidth - thumbWidth, 0);

    return percentValue * maxThumbPosition;
  });

  const calculateScrollPercent = useCallback(
    (xPosition: number, smooth?: boolean) => {
      const track = trackRef.current;
      if (!track) return;

      const trackBounds = track.getBoundingClientRect();

      // Calculate how far the thumb can move (track width minus thumb width)
      const maxThumbPosition = Math.max(trackWidth - thumbWidth, 0);

      // Convert absolute x position to position within the track
      const pointerPositionInTrack = xPosition - trackBounds.left;

      // Center the thumb on the pointer and constrain within bounds
      const thumbCenterPosition = Math.min(
        Math.max(pointerPositionInTrack - thumbWidth / 2, 0),
        maxThumbPosition
      );

      // Convert thumb position to scroll percentage (0-1)
      const percent =
        maxThumbPosition > 0 ? thumbCenterPosition / maxThumbPosition : 0;

      onScrollPercentChange(percent, smooth);
    },
    [onScrollPercentChange, thumbWidth, trackWidth]
  );

  const handleDrag = useCallback(
    (
      _event: MouseEvent | TouchEvent | PointerEvent,
      info: { point: { x: number } }
    ) => {
      calculateScrollPercent(info.point.x);
    },
    [calculateScrollPercent]
  );

  const handleTrackClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;

      calculateScrollPercent(event.clientX, true);
    },
    [calculateScrollPercent]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const currentPercent = scrollPercent.get();
      let newPercent = currentPercent;

      // Accessibility interactions
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          newPercent = Math.max(0, currentPercent - 0.1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          newPercent = Math.min(1, currentPercent + 0.1);
          break;
        case 'Home':
          event.preventDefault();
          newPercent = 0;
          break;
        case 'End':
          event.preventDefault();
          newPercent = 1;
          break;
        case 'PageUp':
          event.preventDefault();
          newPercent = Math.max(0, currentPercent - 0.25);
          break;
        case 'PageDown':
          event.preventDefault();
          newPercent = Math.min(1, currentPercent + 0.25);
          break;
        default:
          return;
      }

      onScrollPercentChange(newPercent, true);
    },
    [scrollPercent, onScrollPercentChange]
  );

  return (
    <ScrollBarTrack
      ref={trackRef}
      onClick={handleTrackClick}
      data-adobe-id={null}
      data-analytics-id={null}
      data-testid="gallery-scrollbar-track"
    >
      <motion.div
        className="absolute top-0 h-full rounded-full bg-background-muted-medium"
        style={{
          width: thumbWidth,
          x: thumbPosition
        }}
        drag="x"
        dragConstraints={trackRef}
        dragElastic={false}
        dragMomentum={false}
        onDragStart={onDragStart}
        onDrag={handleDrag}
        onDragEnd={onDragEnd}
        role="scrollbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(scrollPercent.get() * 100)}
        aria-label="Gallery scroll position"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      />
    </ScrollBarTrack>
  );
}
